'use client';

import type { Reducer } from 'react';
import * as React from 'react';
import { useCallback, useMemo, useReducer } from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import type { Scalars } from '@frameio/graphql-client/generated';
import type { FeedbackDialogVariant } from '../../components/FeedbackDialog/FeedbackDialog';
import { useActiveViewableAssetId } from '../../hooks/useActiveViewableAssetId';
import { useActiveAssetId } from '../ActiveEntitiesContext';
import { useIsFullscreenActive } from '../AppStateContext/appStateSelectors';

/**
 * Modal Reducer
 */

type ModalState =
  | {
      isOpen: true;
      modalType: 'AccountLockDialog';
    }
  | {
      isOpen: true;
      modalType: 'FeedbackDialog';
      variant: FeedbackDialogVariant;
    }
  | {
      isOpen: true;
      modalType: 'LinkAssetToPremiereSequenceDialog';
      assetId: string;
    }
  | {
      isOpen: true;
      modalType: 'VersionStack';
      activeVersionStackId: Scalars['ID']['output'];
    }
  | {
      isOpen: true;
      modalType: 'WatermarkTemplateSelection';
      selectedWatermarkTemplateId?: string;
      dialogHeader: string;
      canManageTemplates?: boolean;
      onTemplateSelect: (template: { id: string; name: string }) => void;
      workspaceId?: string;
    }
  | {
      isOpen: true;
      modalType: 'TransferError';
      title: string;
      details: string;
    }
  | {
      isOpen: true;
      modalType: 'InteractiveEligibleDialog';
      assetIds: [string, ...string[]];
    }
  | {
      isOpen: true;
      modalType: 'OpenOnDesktop';
      projectId: string;
      assetId?: string;
    }
  | {
      isOpen: false;
    };

type ModalStateActions =
  | {
      type: 'OPEN_MODAL';
      payload: {
        modalType: 'AccountLockDialog';
      };
    }
  | {
      type: 'OPEN_MODAL';
      payload: {
        modalType: 'LinkAssetToPremiereSequenceDialog';
        assetId: string;
      };
    }
  | {
      type: 'OPEN_MODAL';
      payload: {
        modalType: 'FeedbackDialog';
        variant: FeedbackDialogVariant;
      };
    }
  | {
      type: 'OPEN_MODAL';
      payload: {
        modalType: 'VersionStack';
        activeVersionStackId: Scalars['ID']['output'];
      };
    }
  | {
      type: 'OPEN_MODAL';
      payload: {
        modalType: 'WatermarkTemplateSelection';
        selectedWatermarkTemplateId?: string;
        dialogHeader: string;
        canManageTemplates?: boolean;
        onTemplateSelect: (template: { id: string; name: string }) => void;
        workspaceId?: string;
      };
    }
  | {
      type: 'OPEN_MODAL';
      payload: {
        modalType: 'TransferError';
        title: string;
        details: string;
      };
    }
  | {
      type: 'OPEN_MODAL';
      payload: {
        modalType: 'InteractiveEligibleDialog';
        assetIds: [string, ...string[]];
      };
    }
  | {
      type: 'OPEN_MODAL';
      payload: {
        modalType: 'OpenOnDesktop';
        projectId: string;
        assetId?: string;
      };
    }
  | {
      type: 'APPEND_ASSET_TO_INTERACTIVE_ELIGIBLE_MODAL';
      payload: {
        assetId: string;
      };
    }
  | {
      type: 'CLOSE_MODAL';
    };

type ModalStateContextValue = ModalState & {
  dispatch: React.Dispatch<ModalStateActions>;
};

const modalReducer: Reducer<ModalState, ModalStateActions> = (
  state,
  action
): ModalState => {
  if (action.type === 'OPEN_MODAL') {
    return {
      isOpen: true,
      ...action.payload,
    };
  }

  if (action.type === 'CLOSE_MODAL') {
    return {
      isOpen: false,
    };
  }

  if (action.type === 'APPEND_ASSET_TO_INTERACTIVE_ELIGIBLE_MODAL') {
    if (state.isOpen && state.modalType === 'InteractiveEligibleDialog') {
      return {
        ...state,
        assetIds: [...state.assetIds, action.payload.assetId],
      };
    }
    return state;
  }

  return state;
};

const DEFAULT_MODAL_STATE: ModalState = {
  isOpen: false,
};

/**
 * Modal Context
 */

const DEFAULT_MODAL_VALUE: ModalStateContextValue = {
  ...DEFAULT_MODAL_STATE,
  dispatch: noop,
};

const ModalContext = createContext<ModalStateContextValue>(DEFAULT_MODAL_VALUE);

export const ModalContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(modalReducer, DEFAULT_MODAL_VALUE);

  const value = useMemo(
    () => ({
      ...state,
      dispatch,
    }),
    [state, dispatch]
  );

  return (
    <ModalContext.Provider value={value}>
      <>{children}</>
    </ModalContext.Provider>
  );
};

export const useVersionStackModalContext = () => {
  const dispatch = useContextSelector(ModalContext, (value) => value.dispatch);

  const isVersionStackModalOpen = useContextSelector(
    ModalContext,
    (value) => value?.isOpen && value?.modalType === 'VersionStack'
  );

  const activeVersionStackId = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'VersionStack'
      ? value?.activeVersionStackId
      : null
  );

  const toggleVersionStackModal = useCallback(
    (versionStackId?: Scalars['ID']['output']) =>
      isVersionStackModalOpen
        ? dispatch({ type: 'CLOSE_MODAL' })
        : dispatch({
            type: 'OPEN_MODAL',
            payload: {
              modalType: 'VersionStack',
              activeVersionStackId: versionStackId!,
            },
          }),
    [dispatch, isVersionStackModalOpen]
  );

  return {
    isVersionStackModalOpen,
    toggleVersionStackModal,
    activeVersionStackId,
  };
};

export const useWatermarkModalContext = () => {
  const dispatch = useContextSelector(ModalContext, (value) => value.dispatch);

  const isOpen = useContextSelector(
    ModalContext,
    (value) => value.isOpen && value?.modalType === 'WatermarkTemplateSelection'
  );

  const activeTemplateId = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'WatermarkTemplateSelection'
      ? value?.selectedWatermarkTemplateId
      : null
  );

  const dialogHeader = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'WatermarkTemplateSelection'
      ? value?.dialogHeader
      : null
  );

  const canManageTemplates = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'WatermarkTemplateSelection'
      ? value?.canManageTemplates
      : null
  );

  const onTemplateSelect = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'WatermarkTemplateSelection'
      ? value?.onTemplateSelect
      : noop
  );

  const workspaceId = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'WatermarkTemplateSelection'
      ? value?.workspaceId
      : undefined
  );

  const open = useCallback(
    ({
      selectedWatermarkTemplateId,
      dialogHeader: header,
      canManageTemplates: canManage,
      onTemplateSelect: onSelectTemplate,
      workspaceId: workspaceIdInput,
    }: {
      selectedWatermarkTemplateId?: string;
      dialogHeader: string;
      canManageTemplates?: boolean;
      onTemplateSelect: (template: { id: string; name: string }) => void;
      workspaceId?: string;
    }) =>
      dispatch({
        type: 'OPEN_MODAL',
        payload: {
          modalType: 'WatermarkTemplateSelection',
          selectedWatermarkTemplateId,
          dialogHeader: header,
          canManageTemplates: canManage ?? true,
          onTemplateSelect: onSelectTemplate,
          workspaceId: workspaceIdInput,
        },
      }),
    [dispatch]
  );

  const close = useCallback(
    () =>
      dispatch({
        type: 'CLOSE_MODAL',
      }),
    [dispatch]
  );

  return {
    isOpen,
    open,
    close,
    activeTemplateId,
    canManageTemplates,
    dialogHeader,
    onTemplateSelect,
    workspaceId,
  };
};

export const useTransferErrorModalContext = () => {
  const dispatch = useContextSelector(ModalContext, (value) => value.dispatch);

  const isTransferErrorModalOpen = useContextSelector(
    ModalContext,
    (value) => value?.isOpen && value?.modalType === 'TransferError'
  );

  const openTransferErrorModal = ({
    title,
    details,
  }: {
    title?: string;
    details?: string;
  }) =>
    dispatch({
      type: 'OPEN_MODAL',
      payload: {
        modalType: 'TransferError',
        title: title || '',
        details: details || '',
      },
    });

  const closeTransferErrorModal = () =>
    dispatch({
      type: 'CLOSE_MODAL',
    });

  const title = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'TransferError' ? value?.title : ''
  );

  const details = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'TransferError' ? value?.details : ''
  );

  return {
    isTransferErrorModalOpen,
    openTransferErrorModal,
    closeTransferErrorModal,
    title,
    details,
  };
};

export const useFeedbackDialogContext = () => {
  const dispatch = useContextSelector(ModalContext, (value) => value.dispatch);
  const isFullscreenActive = useIsFullscreenActive();

  const isFeedbackDialogOpen = useContextSelector(
    ModalContext,
    (value) => value?.isOpen && value?.modalType === 'FeedbackDialog'
  );

  const variant = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'FeedbackDialog'
      ? (value?.variant as FeedbackDialogVariant)
      : null
  );

  const openFeedbackDialog = useCallback(
    (dialogVariant: FeedbackDialogVariant) => {
      /**
       * V4BG-4171: Modals are not displaying properly when in fullscreen mode,
       * so we need to exit fullscreen before opening the feedback dialog.
       **/
      if (isFullscreenActive && document.exitFullscreen) {
        document.exitFullscreen();
      }

      dispatch({
        type: 'OPEN_MODAL',
        payload: {
          modalType: 'FeedbackDialog',
          variant: dialogVariant,
        },
      });
    },
    [dispatch, isFullscreenActive]
  );

  const closeFeedbackDialog = useCallback(
    () =>
      dispatch({
        type: 'CLOSE_MODAL',
      }),
    [dispatch]
  );

  return {
    isFeedbackDialogOpen,
    openFeedbackDialog,
    closeFeedbackDialog,
    variant,
  };
};

export const useAccountLockDialogContext = () => {
  const dispatch = useContextSelector(ModalContext, (value) => value.dispatch);

  const isAccountLockDialogOpen = useContextSelector(
    ModalContext,
    (value) => value?.isOpen && value?.modalType === 'AccountLockDialog'
  );

  const openAccountLockDialog = useCallback(
    () =>
      dispatch({
        type: 'OPEN_MODAL',
        payload: {
          modalType: 'AccountLockDialog',
        },
      }),
    [dispatch]
  );

  const closeAccountLockDialog = useCallback(
    () =>
      dispatch({
        type: 'CLOSE_MODAL',
      }),
    [dispatch]
  );

  return {
    isAccountLockDialogOpen,
    openAccountLockDialog,
    closeAccountLockDialog,
  };
};

export const useLinkAssetToPremiereSequenceDialogContext = () => {
  const dispatch = useContextSelector(ModalContext, (value) => value.dispatch);
  const activeAssetId = useActiveAssetId();
  const viewableAssetId = useActiveViewableAssetId();
  const fallbackAssetId = viewableAssetId || activeAssetId;

  const isLinkAssetToPremiereSequenceDialogOpen = useContextSelector(
    ModalContext,
    (value) =>
      value?.isOpen && value?.modalType === 'LinkAssetToPremiereSequenceDialog'
  );

  const assetId = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'LinkAssetToPremiereSequenceDialog'
      ? value?.assetId
      : fallbackAssetId
  );

  const openLinkAssetToPremiereSequenceDialog = useCallback(
    (assetIdArg: string) =>
      dispatch({
        type: 'OPEN_MODAL',
        payload: {
          modalType: 'LinkAssetToPremiereSequenceDialog',
          assetId: assetIdArg,
        },
      }),
    [dispatch]
  );

  const closeLinkAssetToPremiereSequenceDialog = useCallback(
    () =>
      dispatch({
        type: 'CLOSE_MODAL',
      }),
    [dispatch]
  );

  return {
    assetId,
    isLinkAssetToPremiereSequenceDialogOpen,
    openLinkAssetToPremiereSequenceDialog,
    closeLinkAssetToPremiereSequenceDialog,
  };
};

export const useOpenOnDesktopDialogContext = () => {
  const dispatch = useContextSelector(ModalContext, (value) => value.dispatch);

  const isOpen = useContextSelector(
    ModalContext,
    (value) => value.isOpen && value.modalType === 'OpenOnDesktop'
  );

  const projectId = useContextSelector(ModalContext, (value) =>
    value.isOpen && value.modalType === 'OpenOnDesktop' ? value.projectId : ''
  );

  const assetId = useContextSelector(ModalContext, (value) =>
    value.isOpen && value.modalType === 'OpenOnDesktop'
      ? value.assetId
      : undefined
  );

  const open = useCallback(
    ({
      projectId: pId,
      assetId: aId,
    }: {
      projectId: string;
      assetId?: string;
    }) =>
      dispatch({
        type: 'OPEN_MODAL',
        payload: {
          modalType: 'OpenOnDesktop',
          projectId: pId,
          assetId: aId,
        },
      }),
    [dispatch]
  );

  const close = useCallback(
    () => dispatch({ type: 'CLOSE_MODAL' }),
    [dispatch]
  );

  return { isOpen, projectId, assetId, open, close };
};

export const useInteractiveEligibleDialogContext = () => {
  const dispatch = useContextSelector(ModalContext, (value) => value.dispatch);

  const isInteractiveEligibleDialogOpen = useContextSelector(
    ModalContext,
    (value) => value?.isOpen && value?.modalType === 'InteractiveEligibleDialog'
  );

  const assetIds = useContextSelector(ModalContext, (value) =>
    value.isOpen && value?.modalType === 'InteractiveEligibleDialog'
      ? value?.assetIds ?? []
      : []
  );

  const openInteractiveEligibleDialog = useCallback(
    (assetIdsArg: [string, ...string[]]) => {
      dispatch({
        type: 'OPEN_MODAL',
        payload: {
          modalType: 'InteractiveEligibleDialog',
          assetIds: assetIdsArg,
        },
      });
    },
    [dispatch]
  );

  const closeInteractiveEligibleDialog = useCallback(
    () =>
      dispatch({
        type: 'CLOSE_MODAL',
      }),
    [dispatch]
  );

  const appendAssetToInteractiveEligibleModal = useCallback(
    (assetId: string) => {
      if (isInteractiveEligibleDialogOpen) {
        // If dialog is already open, append the asset ID to the existing array
        dispatch({
          type: 'APPEND_ASSET_TO_INTERACTIVE_ELIGIBLE_MODAL',
          payload: { assetId },
        });
      } else {
        // If dialog is not open, open it with the new asset ID
        openInteractiveEligibleDialog([assetId]);
      }
    },
    [isInteractiveEligibleDialogOpen, openInteractiveEligibleDialog, dispatch]
  );

  return {
    assetIds,
    isInteractiveEligibleDialogOpen,
    openInteractiveEligibleDialog,
    closeInteractiveEligibleDialog,
    appendAssetToInteractiveEligibleModal,
  };
};
